import { useState, useEffect, useCallback } from 'react';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { IconMail, IconArchive, IconTrash } from '@tabler/icons-react';
import {
  listQuoteRequests,
  archiveQuoteRequest,
  deleteQuoteRequest,
  resendQuoteEmails,
} from '../services/quotesService';
import { getUserNames } from '../services/usersService';
import type { QuoteRequest } from '../types';

type StatusFilter = 'all' | 'pending' | 'sent' | 'archived';

export default function QuotesPage() {
  const [items, setItems] = useState<QuoteRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [resendingId, setResendingId] = useState<string | null>(null);
  const [userNames, setUserNames] = useState<Record<string, string>>({});

  /** Fetch from Firestore; reset=true clears existing list */
  const fetchPage = useCallback(
    async (cursor: QueryDocumentSnapshot<DocumentData> | null) => {
      setLoading(true);
      try {
        const result = await listQuoteRequests({
          pageSize: 50,
          lastDoc: cursor,
          statusFilter,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        });
        setItems(prev => (cursor === null ? result.items : [...prev, ...result.items]));
        setLastDoc(result.lastDoc);
        setHasMore(result.hasMore);
      } catch (err) {
        console.error('Failed to load quote requests:', err);
        alert('Failed to load quote requests.');
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, startDate, endDate]
  );

  // Reset and reload when filters change
  useEffect(() => {
    setLastDoc(null);
    setItems([]);
    setHasMore(false);
    fetchPage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, startDate, endDate]);

  // Fetch display names for user IDs not yet resolved
  useEffect(() => {
    const missing = [...new Set(items.map(q => q.userId))].filter(id => !(id in userNames));
    if (missing.length === 0) return;
    getUserNames(missing).then(names => setUserNames(prev => ({ ...prev, ...names })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const loadMore = useCallback(() => {
    fetchPage(lastDoc);
  }, [fetchPage, lastDoc]);

  async function handleResend(quote: QuoteRequest) {
    if (!confirm('Resend emails for this quote request?')) return;
    setResendingId(quote.id);
    try {
      await resendQuoteEmails(quote.id);
      // Reload from top after resend
      const result = await listQuoteRequests({
        pageSize: 50,
        lastDoc: null,
        statusFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setItems(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to resend emails:', err);
      alert('Failed to resend emails. Please try again.');
    } finally {
      setResendingId(null);
    }
  }

  async function handleArchive(quote: QuoteRequest) {
    if (!confirm('Archive this quote request?')) return;
    try {
      await archiveQuoteRequest(quote.id);
      const result = await listQuoteRequests({
        pageSize: 50,
        lastDoc: null,
        statusFilter,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
      });
      setItems(result.items);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error('Failed to archive quote request:', err);
      alert('Failed to archive quote request.');
    }
  }

  async function handleDelete(quote: QuoteRequest) {
    if (!confirm('Delete this quote request? This action cannot be undone.')) return;
    try {
      await deleteQuoteRequest(quote.id);
      setItems(prev => prev.filter(q => q.id !== quote.id));
    } catch (err) {
      console.error('Failed to delete quote request:', err);
      alert('Failed to delete quote request.');
    }
  }

  function statusBadge(status: QuoteRequest['status']) {
    switch (status) {
      case 'pending':
        return <span className="badge bg-warning text-white">Pending</span>;
      case 'sent':
        return <span className="badge bg-success text-white">Sent</span>;
      case 'archived':
        return <span className="badge bg-secondary text-white">Archived</span>;
    }
  }

  function formatDate(iso: string | undefined): string {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString();
  }

  function truncateId(id: string): string {
    return id.length > 8 ? `${id.slice(0, 8)}…` : id;
  }

  return (
    <div className="page-header">
      <div className="row g-2 align-items-center">
        <div className="col">
          <div className="page-pretitle">Market</div>
          <h2 className="page-title">Quote Requests</h2>
        </div>
      </div>

      {/* Filters */}
      <div className="card mt-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              >
                <option value="all">All</option>
                <option value="pending">Pending</option>
                <option value="sent">Sent</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">From date</label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">To date</label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card mt-3">
        <div className="table-responsive">
          <table className="table table-vcenter card-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Location</th>
                <th>Service</th>
                <th>Suppliers</th>
                <th>Status</th>
                <th>Email Sent</th>
                <th className="w-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-4">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading…
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="empty">
                      <p className="empty-title">No quote requests found</p>
                      <p className="empty-subtitle text-muted">Try adjusting your filters</p>
                    </div>
                  </td>
                </tr>
              ) : (
                items.map(quote => (
                  <tr key={quote.id}>
                    <td>{formatDate(quote.requestedAt)}</td>
                    <td>
                      <span title={quote.userId}>
                        {userNames[quote.userId] ?? truncateId(quote.userId)}
                      </span>
                    </td>
                    <td>
                      {quote.location.district} / {quote.location.county}
                      {quote.location.parish && (
                        <span className="text-muted"> / {quote.location.parish}</span>
                      )}
                    </td>
                    <td>{quote.serviceName}</td>
                    <td>
                      <span className="badge bg-blue-lt">{quote.supplierIds.length}</span>
                    </td>
                    <td>{statusBadge(quote.status)}</td>
                    <td>{formatDate(quote.emailSentAt)}</td>
                    <td>
                      <div className="btn-list flex-nowrap">
                        {quote.status !== 'archived' && (
                          <button
                            className="btn btn-icon"
                            title="Resend Emails"
                            onClick={() => handleResend(quote)}
                            disabled={resendingId === quote.id}
                          >
                            {resendingId === quote.id ? (
                              <span className="spinner-border spinner-border-sm" />
                            ) : (
                              <IconMail className="icon" />
                            )}
                          </button>
                        )}
                        {quote.status !== 'archived' && (
                          <button
                            className="btn btn-icon"
                            title="Archive"
                            onClick={() => handleArchive(quote)}
                          >
                            <IconArchive className="icon" />
                          </button>
                        )}
                        <button
                          className="btn btn-icon text-danger"
                          title="Delete"
                          onClick={() => handleDelete(quote)}
                        >
                          <IconTrash className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="text-center mt-3">
          <button className="btn btn-outline-secondary" onClick={loadMore} disabled={loading}>
            {loading ? <span className="spinner-border spinner-border-sm" /> : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
}
